import { CheckMarkIcon, TimeIcon } from '@braskai/icons';
import { Typography, useDeviceType } from '@braskai/ui-kit';

import { Box, Chip, Stack } from '@mui/material';

import { useProducts } from '@/entities/pricing';

import { TranslationKey, useInterfaceLanguage } from '@/shared/i18n';
import { formatInterfaceCurrency, formatInterfaceNumber } from '@/shared/i18n/formatLocale';
import { PaymentInterval, PlanCategory, Products } from '@/shared/types/Billing';

import { SBadge, SCard, SCards, SMinutes } from './styled';

type PlanCard = {
  name: TranslationKey;
  category: PlanCategory;
  features: TranslationKey[];
};

const DUBBING = 'paidTrial.planFeatures.dubbing';
const LIP_SYNC = 'pricing.compare.row.lipSync';
const SRT = 'pricing.compare.row.srt';

const PLAN_CARDS: PlanCard[] = [
  { name: 'pricing.plan.creator', category: PlanCategory.Creator, features: ['paidTrial.planFeatures.dubbingOnly'] },
  { name: 'pricing.plan.creatorPro', category: PlanCategory.CreatorPro, features: [DUBBING, LIP_SYNC, SRT] },
  {
    name: 'pricing.plan.business',
    category: PlanCategory.Business,
    features: [DUBBING, LIP_SYNC, 'pricing.compare.row.onscreen', SRT],
  },
];

const getMonthlyPlan = (products: Products | undefined, category: PlanCategory) => {
  const plans =
    products?.plan
      .filter((plan) => plan.category === category && !plan.is_archived)
      .sort((a, b) => (a.metadata.bundle_position || 0) - (b.metadata.bundle_position || 0)) ?? [];
  const plan = plans.find((item) => item.metadata.bundle_value_default) ?? plans[0];

  return {
    minutes: Number(plan?.metadata.bundle_value) || undefined,
    price: plan?.prices.find(
      (price) => price.status === 'active' && price.recurring?.interval === PaymentInterval.Month,
    ),
  };
};

export const PlanFeaturesContent = () => {
  const { isMobile } = useDeviceType();
  const { locale, t } = useInterfaceLanguage();
  const { availableProducts } = useProducts(true, { showErrors: false });

  return (
    <SCards>
      {PLAN_CARDS.map(({ name, category, features }) => {
        const { minutes, price } = getMonthlyPlan(availableProducts, category);
        const isPopular = category === PlanCategory.CreatorPro;
        const minutesLabel = minutes
          ? t('pricing.minutes.included', { count: formatInterfaceNumber(locale, minutes) })
          : null;

        const title = (
          <Typography color="grey.900" variant="title-4-sb">
            {t(name)}
          </Typography>
        );
        const priceLine = price && (
          <Stack alignItems="flex-end" direction="row" flexShrink={0} gap={4}>
            <Typography color="grey.900" variant="title-2-sb">
              {formatInterfaceCurrency(locale, price.unit_amount, price.currency, { compact: true })}
            </Typography>
            <Typography color="grey.500" sx={{ paddingBottom: 2 }} variant="body-2-md">
              {t('pricing.price.perMonth')}
            </Typography>
          </Stack>
        );

        return (
          <SCard key={category} $isPopular={isPopular} data-testid={`plan-features-card-${category}`}>
            {isPopular && (
              <SBadge>
                <Chip label={t('pricing.plan.mostPopular')} color="primary" size="small" />
              </SBadge>
            )}
            {isMobile ? (
              <Stack alignItems="flex-start" direction="row" gap={12} justifyContent="space-between">
                <Stack gap={4}>
                  {title}
                  {minutesLabel && (
                    <Typography color="grey.500" variant="body-2-md">
                      {minutesLabel}
                    </Typography>
                  )}
                </Stack>
                {priceLine}
              </Stack>
            ) : (
              <Stack gap={16}>
                {title}
                {priceLine}
                {minutesLabel && (
                  <SMinutes>
                    <TimeIcon height={20} width={20} />
                    <Typography color="common.black" variant="body-2-md">
                      {minutesLabel}
                    </Typography>
                  </SMinutes>
                )}
              </Stack>
            )}
            <Stack gap={12}>
              {features.map((feature) => (
                <Stack key={feature} alignItems="flex-start" direction="row" gap={8}>
                  <Box sx={{ color: 'primary.600', display: 'flex', flexShrink: 0 }}>
                    <CheckMarkIcon height={20} width={20} />
                  </Box>
                  <Typography color="grey.900" variant="body-2">
                    {t(feature)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </SCard>
        );
      })}
    </SCards>
  );
};
